// Audit-to-audit diff computation. Takes a current findings list and a previous findings list,
// builds a (cid, checkId) → previous-finding index, and decorates each current finding with a
// diff entry. Pure function — no DB or network access.

import {
  Finding,
  FindingDiff,
  DiffTransition,
  AuditDiffEntry,
  AuditDiffResult,
  AuditDiffRunRef,
  severityRank
} from './types';

function key(f: Pick<Finding, 'cid' | 'checkId'>): string {
  return `${f.cid}|${f.checkId}`;
}

// Computes "did the underlying audit result change?" — i.e., something other than user-driven
// triage moved. Severity, status (available ↔ unavailable), and statusReason all count.
// Note: we intentionally don't diff evidence payloads or LLM verdict text in v1 — those would
// produce noisy "changed" verdicts on every re-run since stats / verdicts shift naturally.
function underlyingChanged(current: Finding, prev: Finding): boolean {
  if (current.severity !== prev.severity) return true;
  if (current.status !== prev.status) return true;
  return false;
}

function anyChanged(current: Finding, prev: Finding): boolean {
  if (underlyingChanged(current, prev)) return true;
  if (current.triage !== prev.triage) return true;
  return false;
}

export function decorateWithDiff(current: Finding[], previous: Finding[] | null): Finding[] {
  if (previous === null) {
    // No previous audit — every finding is "new" relative to nothing. Surface as null diff so
    // the frontend can decide whether to show a "first audit" banner without per-card noise.
    return current.map(f => ({ ...f, diff: null }));
  }
  const prevByKey = new Map<string, Finding>();
  for (const p of previous) prevByKey.set(key(p), p);

  return current.map<Finding>(f => {
    const prev = prevByKey.get(key(f));
    if (!prev) {
      const diff: FindingDiff = { status: 'new' };
      return { ...f, diff };
    }
    if (!anyChanged(f, prev)) {
      const diff: FindingDiff = { status: 'unchanged' };
      return { ...f, diff };
    }
    const diff: FindingDiff = {
      status: 'changed',
      previousSeverity: prev.severity,
      previousTriage: prev.triage,
      previousStatus: prev.status,
      underlyingChanged: underlyingChanged(f, prev)
    };
    return { ...f, diff };
  });
}

// ----- Slice 6: dedicated audit-to-audit diff -----
// Uses the canonical severityRank from types.ts (na:0, no_issue:1, small:2, medium:3, large:4).
// "Flagged" means rank >= 2 (small/medium/large — a real problem). no_issue and na are both
// below that threshold. Unavailable findings carry severity 'na' (rank 0) — handled specially
// below so we never report a fix as "resolved" when we actually just lost the ability to assess.
function isUnavailable(f: Finding): boolean {
  return f.status === 'unavailable';
}

function classify(current: Finding, prior: Finding): DiffTransition {
  const rc = severityRank(current.severity);
  const rp = severityRank(prior.severity);
  // Flagged = rank >= 2 (small/medium/large). no_issue (1) and na (0) are not flagged.
  const flaggedPrior = rp >= 2;

  // Genuinely resolved: was a real problem, now explicitly clean (no_issue, not just unassessable).
  if (flaggedPrior && current.severity === 'no_issue') return 'resolved';
  if (rc > rp) return 'worsened';
  if (rc < rp) {
    // Severity "dropped" but it's because we lost visibility (current unavailable) — not a real
    // improvement. Keep it surfaced as still-open rather than claiming progress.
    if (isUnavailable(current)) return 'still_open';
    return 'improved';
  }
  // Equal rank.
  return rc >= 2 ? 'still_open' : 'unchanged';
}

function metaFrom(
  f: Finding
): Pick<AuditDiffEntry, 'cid' | 'platform' | 'template' | 'section' | 'checkId' | 'templateRow'> {
  return {
    cid: f.cid,
    platform: f.platform,
    template: f.template,
    section: f.section,
    checkId: f.checkId,
    templateRow: f.templateRow
  };
}

const EMPTY_COUNTS = (): Record<DiffTransition, number> => ({
  resolved: 0,
  worsened: 0,
  improved: 0,
  new: 0,
  removed: 0,
  still_open: 0,
  unchanged: 0
});

// Bidirectional join on (cid, checkId): every pair present in EITHER run produces an entry, so
// Removed findings (present in prior, gone now) are visible — unlike decorateWithDiff, which only
// walks current findings. Pure function.
export function computeAuditDiff(
  current: Finding[],
  prior: Finding[],
  currentRef: AuditDiffRunRef,
  priorRef: AuditDiffRunRef | null
): AuditDiffResult {
  const counts = EMPTY_COUNTS();

  // No prior run → nothing to diff against; report current findings as a 'new'-only baseline.
  if (priorRef === null) {
    const entries = current.map<AuditDiffEntry>(f => ({
      ...metaFrom(f),
      transition: 'new',
      currentSeverity: f.severity,
      priorSeverity: null,
      availabilityChanged: false,
      current: f,
      prior: null
    }));
    counts.new = entries.length;
    return { current: currentRef, prior: null, entries, counts };
  }

  const curByKey = new Map(current.map(f => [key(f), f]));
  const priorByKey = new Map(prior.map(f => [key(f), f]));
  const entries: AuditDiffEntry[] = [];

  // Current findings: matched (classify) or new.
  for (const f of current) {
    const p = priorByKey.get(key(f));
    if (!p) {
      entries.push({
        ...metaFrom(f),
        transition: 'new',
        currentSeverity: f.severity,
        priorSeverity: null,
        availabilityChanged: false,
        current: f,
        prior: null
      });
      continue;
    }
    entries.push({
      ...metaFrom(f),
      transition: classify(f, p),
      currentSeverity: f.severity,
      priorSeverity: p.severity,
      availabilityChanged: isUnavailable(f) !== isUnavailable(p),
      current: f,
      prior: p
    });
  }

  // Prior findings with no current match → removed (the check no longer runs for this cid).
  for (const p of prior) {
    if (curByKey.has(key(p))) continue;
    entries.push({
      ...metaFrom(p),
      transition: 'removed',
      currentSeverity: null,
      priorSeverity: p.severity,
      availabilityChanged: false,
      current: null,
      prior: p
    });
  }

  for (const e of entries) counts[e.transition] += 1;
  return { current: currentRef, prior: priorRef, entries, counts };
}
